import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";

const sel = "block h-7 text-xs border border-gray-300 rounded px-2 bg-white min-w-[120px]";

/** Bulk-drop location: any building › floor › area › room marked on the JOB (not only the
 *  ticket's own rooms). Every level is optional — stop at the floor for a floor drop. */
export default function DropLocationPicker({ projectId, value, onChange }) {
  const [tree, setTree] = useState(null);
  useEffect(() => {
    if (!projectId) return;
    api.get(`/delivery/projects/${projectId}/locations`).then(({ data }) => setTree(data))
      .catch(() => setTree({ buildings: [], rooms: 0 }));
  }, [projectId]);

  const buildings = tree?.buildings || [];
  const bld = useMemo(() => buildings.find((b) => b.building === value.building)
    || (buildings.length === 1 ? buildings[0] : null), [buildings, value.building]);
  const floors = bld?.floors || [];
  const flr = floors.find((f) => f.floor === value.floor) || null;
  const areas = flr?.areas || [];
  const ar = areas.find((a) => a.area === value.area) || null;
  const rooms = ar ? ar.rooms : areas.flatMap((a) => a.rooms);

  // one building on the job → it is implied, the picker starts at the floor
  useEffect(() => {
    if (buildings.length === 1 && value.building !== buildings[0].building) {
      onChange({ ...value, building: buildings[0].building });
    }
  }, [buildings]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (patch) => onChange({ ...value, ...patch });
  const pickRoom = (rk) => {
    const r = rooms.find((x) => x.room_key === rk);
    set({ room_key: rk, room_label: r?.label || "" });
  };
  const noneLabel = (what) => `— any ${what} —`;

  if (!tree) return <span className="text-[11px] text-gray-400">loading places…</span>;
  return (
    <div className="flex flex-wrap items-end gap-2" data-testid="delivery-drop-location">
      {buildings.length > 1 && (
        <label className="text-[10px] uppercase tracking-wide text-gray-600">Building
          <select value={value.building} className={sel} data-testid="delivery-drop-building"
                  onChange={(e) => set({ building: e.target.value, floor: "", area: "", room_key: "", room_label: "" })}>
            <option value="">{noneLabel("building")}</option>
            {buildings.map((b) => <option key={b.building} value={b.building}>{b.building || "(no building)"}</option>)}
          </select>
        </label>
      )}
      <label className="text-[10px] uppercase tracking-wide text-gray-600">Floor
        <select value={value.floor} className={sel} data-testid="delivery-drop-floor" disabled={!bld}
                onChange={(e) => set({ floor: e.target.value, area: "", room_key: "", room_label: "" })}>
          <option value="">{noneLabel("floor")}</option>
          {floors.map((f) => <option key={f.floor} value={f.floor}>{f.floor || "(no floor)"}</option>)}
        </select>
      </label>
      <label className="text-[10px] uppercase tracking-wide text-gray-600">Area
        <select value={value.area} className={sel} data-testid="delivery-drop-area" disabled={!flr}
                onChange={(e) => set({ area: e.target.value, room_key: "", room_label: "" })}>
          <option value="">{noneLabel("area")}</option>
          {areas.map((a) => <option key={a.area} value={a.area}>{a.area || "(no area)"}</option>)}
        </select>
      </label>
      <label className="text-[10px] uppercase tracking-wide text-gray-600">Room
        <select value={value.room_key} className={`${sel} max-w-[220px]`} data-testid="delivery-drop-room" disabled={!flr}
                onChange={(e) => pickRoom(e.target.value)}>
          <option value="">{noneLabel("room")}</option>
          {rooms.map((r) => <option key={r.room_key} value={r.room_key}>{r.label}</option>)}
        </select>
      </label>
    </div>
  );
}
